import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Template } from "../../../components/template/template.jsx";
import ReportsService from "./reports-service.js";
import AgGridUtils from "../../../utils/aggrid-utils.js";
import { FaEraser, FaSearch, FaStar, FaRegStar } from "react-icons/fa";
import { Card } from "../../../components/card/card.jsx";
import { AgGridReact } from "ag-grid-react";
import Button from "../../../components/button/button.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import { DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL } from "../../../utils/ag-grid-columns-utils.js";
import { useGlobalKey } from "../../../hooks/use-global-key.js";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {toast} from "react-toastify";
import ProjectUtils from "../../../utils/project-utils.js";
import AgGridLinkCellRenderer from "../../../components/ag-grid/ag-grid-link-cell-renderer.jsx";

const initialFilters = {
    moduleName: "",
    name: "",
};

const getReportUrls = ({path}) => {
    if (!path) return {};

    return {
        urlDev: `/reports/${path}`,
    };
};

const Reports = () => {
    const [filter, setFilter] = useState(initialFilters);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);

    const { data: reports = [], isLoading, refetch: refetchReports } = useQuery({
        queryKey: ['reports'],
        queryFn: ReportsService.list,
        initialData: [],
        placeholderData: keepPreviousData
    });

    const rowData = useMemo(() => {
        return [...reports].sort((a, b) => {
            if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
            return 0;
        });
    }, [reports]);

    const toggleFavorite = async (id) => {

        try {
            await ReportsService.toggleFavorite(id);
            await refetchReports();

            gridRef.current?.api?.ensureIndexVisible(0, 'top');
        } catch (e) {
            console.error(e);
            toast.error("Não foi possível modificar os favoritos.", {autoClose: 1200, pauseOnHover: false});
        }

    };

    const CustomTooltip = ({ value }) => {
        return (
            <div style={{
                background: "#fff",
                border: "1px solid #dddee0",
                padding: "10px",
                borderRadius: "4px",
                maxWidth: "400px",
                whiteSpace: "normal",
                wordBreak: "break-word",
                boxShadow: "0 2px 8px rgba(0,0,0,.15)",
            }}>
                {value}
            </div>
        );
    };

    const columnDefs = useMemo(() => ([
        {
            headerName: "",
            field: "favorite",
            width: 40,
            sortable: false,
            filter: false,
            suppressMenu: true,
            tooltipComponent: CustomTooltip,
            tooltipValueGetter: (params) => {
                const isFav = params.data.favorite;
                const name = params.data.name?.trim();
                const label = name ? `"${name}"` : "relatório";

                return !isFav ? `Adicionar ${label} aos favoritos` : `Remover ${label} dos favoritos`;
            },
            cellRenderer: (params) => {
                const isFav = params.data.favorite;

                const name = params.data.name?.trim();
                const label = name ? `"${name}"` : "relatório";

                return (
                    <div
                        className={`flex justify-center items-center h-full favorite-cell`}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFavorite(params.data.id, label, isFav);
                        }}
                    >
                        {isFav ? <FaStar color={"#ffca28"} size={17}/> : <FaRegStar color={'rgba(58,58,58,0.22)'} size={15}/>}
                    </div>
                );
            }
        },
        { headerName: "Nome", field: "name", flex: 2, filterValueGetter: ({ data }) => `${data?.name ?? ""} ${data?.report_group ?? ""}`, cellRenderer: AgGridLinkCellRenderer, cellRendererParams: {getUrls: getReportUrls, openNewTabOnClick: true }},
        { headerName: "Grupo", field: "report_group", flex: 1, cellRenderer: AgGridLinkCellRenderer, cellRendererParams: {getUrls: getReportUrls, openNewTabOnClick: true} },
        { headerName: "Módulo", field: "module_name", flex: 1, sortable: true, cellRenderer: AgGridLinkCellRenderer, cellRendererParams: {getUrls: getReportUrls, openInNewTabOnClick: true} }
    ]), []);

    const normalize = (value) => {
        return ProjectUtils.removeAccents(value)?.toString().toLowerCase() || "";
    };

    const defaultColDef = useMemo(() => ({
        ...DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL,
        sortable: true,
        filter: true,
        filterParams: {
            textFormatter: normalize
        }
    }), []);

    const handleClear = () => {
        setFilter(initialFilters);
        const api = gridRef.current?.api;

        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    };

    const applyFilter = useCallback(() => {
        if (!gridApi) return;
        const filterModel = {};

        if (filter.name) {
            filterModel.name = {
                type: "contains",
                filter: filter.name
            };
        }

        if (filter.moduleName) {
            filterModel.module_name = {
                type: "contains",
                filter: filter.moduleName
            };
        }

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    useGlobalKey("Enter", applyFilter);
    useGlobalKey("Escape", handleClear);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Relatórios</Template.Title>
            </Template.Header>

            <Template.Body className="space-y-2 flex flex-col h-full">

                <Card>
                    <Card.Body>
                        <div className="flex gap-4 items-end">
                            <InputForm
                                id="name"
                                label="Nome/Grupo"
                                value={filter.name}
                                onChange={({ target: { id, value } }) => setFilter(prev => ({ ...prev, [id]: value }))}
                                classNameWrapper="flex-1"
                                autocomplete={'off'}
                            />

                            <InputForm
                                id="moduleName"
                                label="Módulo"
                                value={filter.moduleName}
                                onChange={({ target: { id, value } }) => setFilter(prev => ({ ...prev, [id]: value }))}
                                classNameWrapper="flex-1"
                                autocomplete={'off'}
                            />

                            <div className="flex gap-2">
                                <Button target="primary-outline" title="Limpar" onClick={handleClear}>
                                    <FaEraser/>
                                </Button>

                                <Button title="Buscar" onClick={applyFilter}>
                                    <FaSearch/>
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="flex-1">
                    <Card.Body className="w-full">
                        <div className="h-[calc(100vh-153px)] w-full">
                            <AgGridReact
                                ref={gridRef}
                                getRowId={(params) => String(params.data.id)}
                                immutableData={true}
                                rowData={rowData}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                paginationPageSize={50}
                                paginationPageSizeSelector={[10, 50, 100, 500]}
                                onGridReady={(params) => {setGridApi(params.api);}}
                                onFilterChanged={AgGridUtils.filterChangedNoRowsOverlay}
                                animateRows={false}
                                suppressCellFocus
                                gridOptions={{ localeText: AgGridUtils.localization() }}
                                theme={AgGridUtils.generalTheme()}
                                rowStyle={{ cursor: 'pointer' }}
                                tooltipShowDelay={400}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default Reports;
